import { PermissionGuard } from '@/components/PermissionGuard';
import { fadeUp } from '@/lib/animations';
import { formatDateShort, formatPrice } from '@/lib/format';
import {
  formatTravelEsimData,
  formatTravelEsimDuration,
  travelEsimLineStatus,
  travelEsimStatusClasses,
} from '@/lib/travel-esim';
import { cn } from '@/lib/utils';
import type { TravelEsimDetail, TravelEsimStayState } from '@octave/contracts';
import { motion } from 'framer-motion';
import { Plus } from 'lucide-react';
import { Link } from 'react-router-dom';

interface TravelEsimPlansCardProps {
  esim: TravelEsimDetail;
  tenantSlug: string;
  /** `YYYY-MM-DD`, to classify each stay. */
  today: string;
}

const STAY_LABEL: Record<TravelEsimStayState, string> = {
  upcoming: 'À venir',
  ongoing: 'En cours',
  past: 'Terminé',
};

function stayStateOf(
  departureDate: string,
  returnDate: string,
  today: string,
): TravelEsimStayState {
  if (today < departureDate) return 'upcoming';
  if (today > returnDate) return 'past';
  return 'ongoing';
}

/** « Mes forfaits »: every plan loaded on the eSIM, past stays greyed out. */
export function TravelEsimPlansCard({ esim, tenantSlug, today }: TravelEsimPlansCardProps) {
  const orderUrl =
    esim.beneficiary.kind === 'LINE'
      ? `/o/${tenantSlug}/catalog?tab=travel-esim&line=${esim.beneficiary.lineId}`
      : `/o/${tenantSlug}/catalog?tab=travel-esim`;

  return (
    <motion.div variants={fadeUp} className="rounded-2xl bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-center justify-between gap-4">
        <h2 className="text-base font-semibold text-slate-900">Mes forfaits</h2>
        <PermissionGuard permission="write">
          <Link
            to={orderUrl}
            className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-all hover:bg-emerald-700 hover:shadow-md"
          >
            <Plus className="h-3.5 w-3.5" />
            Ajouter un forfait
          </Link>
        </PermissionGuard>
      </div>

      {esim.plans.length === 0 ? (
        <p className="py-6 text-center text-sm text-slate-400">
          Aucun forfait chargé sur cette eSIM.
        </p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {esim.plans.map((plan) => {
            const { metadata } = plan.item;
            if (metadata.type !== 'TRAVEL_ESIM') return null;
            const state = stayStateOf(metadata.departureDate, metadata.returnDate, today);
            const status = travelEsimLineStatus(metadata);
            return (
              <li
                key={plan.item.id}
                className={cn(
                  'flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between',
                  state === 'past' && 'opacity-50',
                )}
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900">
                    {metadata.country} · {formatTravelEsimData(metadata.dataLimitGb)}{' '}
                    pendant {formatTravelEsimDuration(metadata.stayDurationDays)}
                  </p>
                  <p className="text-xs text-slate-500">
                    Du {formatDateShort(metadata.departureDate)} au{' '}
                    {formatDateShort(metadata.returnDate)} ·{' '}
                    {formatPrice(metadata.totalPriceCents)} HT ·{' '}
                    <Link
                      to={`/o/${tenantSlug}/orders/${plan.orderId}`}
                      className="underline hover:text-slate-700"
                    >
                      Commande {plan.orderNumber}
                    </Link>
                  </p>
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-2">
                  <span
                    className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[10px] font-semibold ${travelEsimStatusClasses(status.tone)}`}
                  >
                    {status.label}
                  </span>
                  <span
                    className={cn(
                      'inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-semibold',
                      state === 'ongoing'
                        ? 'bg-emerald-600 text-white'
                        : state === 'upcoming'
                          ? 'bg-sky-50 text-sky-700'
                          : 'bg-slate-100 text-slate-500',
                    )}
                  >
                    {STAY_LABEL[state]}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </motion.div>
  );
}
